import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Delete from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import {
  validateEmpty,
  validateUrlRoute,
} from "../../../shared/helper/validations";
import PropTypes from "prop-types";

const AuthItem = ({
  auth,
  _id,
  arrayAuth,
  modalDeleteAuth,
  saveAuth,
  userPermissions,
}) => {
  const { t } = useTranslation("apiauths");
  const [editing, setEditing] = useState();
  const [edit, setEdit] = useState();
  const [editUsernameValue, setEditUsernameValue] = useState(false);
  const [editPasswordValue, setEditPasswordValue] = useState(false);
  const [errors, setErrors] = useState();
  const [save, setSave] = useState();
  const [isNew, setIsNew] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    setEditing(auth.editing);
    setErrors({});
    let tempEdit;
    if (auth.editing) {
      if (auth.add) {
        tempEdit = {
          _id: auth._id,
          type: auth.type,
          payload: {
            username: {
              key: auth.payload.username.key,
              value: auth.payload.username.value,
            },
            password: {
              key: auth.payload.password.key,
              value: auth.payload.password.value,
            },
            loginRoute: auth.payload.loginRoute,
            tokenPrefix: auth.payload.tokenPrefix,
          },
          tokenName: auth.tokenName,
          new: auth.new,
          editing: auth.editing,
        };
        delete auth.add;
        delete arrayAuth[_id].add;
      } else {
        tempEdit = {
          _id: auth._id,
          type: edit?.type ?? auth.type,
          payload: {
            username: {
              key: edit?.payload?.username?.key ?? auth.payload.username.key,
              value:
                edit?.payload?.username?.value ?? auth.payload.username.value,
              editValue: false,
            },
            password: {
              key: edit?.payload?.password?.key ?? auth.payload.password.key,
              value:
                edit?.payload?.password?.value ?? auth.payload.password.value,
              editValue: false,
            },
            loginRoute: edit?.payload?.loginRoute ?? auth.payload.loginRoute,
            tokenPrefix: edit?.payload?.tokenPrefix ?? auth.payload.tokenPrefix,
          },
          tokenName: edit?.tokenName ?? auth.tokenName,
          new: edit?.new ?? auth.new,
          editing: auth.editing,
        };
      }
      setEdit(tempEdit);
    }
    if (auth.new) {
      const tempIsNew = {};
      const currentType = normalizeTypeValue(auth.type);

      if (currentType === "http") {
        if (
          !tempEdit.payload?.loginRoute ||
          tempEdit.payload?.loginRoute === ""
        ) {
          tempIsNew.loginRoute = true;
        }
        if (
          !tempEdit.payload?.username?.value ||
          tempEdit.payload?.username?.value === ""
        ) {
          tempIsNew.usernameValue = true;
        }
        if (
          !tempEdit.payload?.password?.value ||
          tempEdit.payload?.password?.value === ""
        ) {
          tempIsNew.passwordValue = true;
        }
        if (
          !tempEdit.payload?.tokenPrefix ||
          tempEdit.payload?.tokenPrefix === ""
        ) {
          tempIsNew.tokenPrefix = true;
        }
        if (!tempEdit.tokenName || tempEdit.tokenName === "") {
          tempIsNew.tokenName = true;
        }
      } else if (currentType === "oauth2.0") {
        if (!tempEdit.payload?.tokenUrl || tempEdit.payload?.tokenUrl === "") {
          tempIsNew.tokenUrl = true;
        }
        if (!tempEdit.payload?.clientId || tempEdit.payload?.clientId === "") {
          tempIsNew.clientId = true;
        }
        if (
          !tempEdit.payload?.clientSecret ||
          tempEdit.payload?.clientSecret === ""
        ) {
          tempIsNew.clientSecret = true;
        }
        if (!tempEdit.payload?.scopes || tempEdit.payload?.scopes === "") {
          tempIsNew.scopes = true;
        }
      }
      setIsNew(tempIsNew);
    } else {
      const tempIsNew = {};
      const currentType = normalizeTypeValue(auth.type);

      if (currentType === "http") {
        tempIsNew.loginRoute = false;
        tempIsNew.usernameValue = false;
        tempIsNew.passwordValue = false;
        tempIsNew.tokenPrefix = false;
        tempIsNew.tokenName = false;
      } else if (currentType === "oauth2.0") {
        tempIsNew.tokenUrl = false;
        tempIsNew.clientId = false;
        tempIsNew.clientSecret = false;
        tempIsNew.scopes = false;
      }
      setIsNew(tempIsNew);
    }
  }, [auth]);

  useEffect(() => {
    if (editing) {
      validate();
    } else {
      setErrors({});
    }
  }, [edit]);

  const normalizeTypeValue = (type) => {
    if (!type) return "http";
    const normalized = type.toLowerCase();
    if (normalized === "http" || normalized === "oauth2.0") {
      return normalized;
    }
    return "http";
  };

  const validate = () => {
    const tempError = {};
    const currentType = normalizeTypeValue(edit?.type);

    if (currentType === "http") {
      // Validación para HTTP
      tempError.loginRoute = validateLoginAuth();
      if (tempError.loginRoute === "") {
        delete tempError.loginRoute;
      }

      if (validateEmpty(edit.payload.username?.key)) {
        delete tempError.usernameKey;
      } else {
        tempError.usernameKey = t("errorEmpty");
      }

      if (editUsernameValue) {
        if (validateEmpty(edit.payload.username?.value)) {
          delete tempError.usernameValue;
        } else {
          if (!isNew.usernameValue) {
            tempError.usernameValue = t("errorEmpty");
          }
        }
      }

      if (validateEmpty(edit.payload.password?.key)) {
        delete tempError.passwordKey;
      } else {
        tempError.passwordKey = t("errorEmpty");
      }

      if (editPasswordValue) {
        if (validateEmpty(edit.payload.password?.value)) {
          delete tempError.passwordValue;
        } else {
          if (!isNew.passwordValue) {
            tempError.passwordValue = t("errorEmpty");
          }
        }
      }
    } else if (currentType === "oauth2.0") {
      // Validación para OAUTH2.0
      if (!validateEmpty(edit.payload.tokenUrl)) {
        tempError.tokenUrl = t("errorEmpty");
      }
      if (!validateEmpty(edit.payload.clientId)) {
        tempError.clientId = t("errorEmpty");
      }
      if (!validateEmpty(edit.payload.clientSecret)) {
        tempError.clientSecret = t("errorEmpty");
      }
      // scopes puede ser opcional, si quieres que sea obligatorio, descomenta la siguiente línea:
      // if (!validateEmpty(edit.payload.scopes)) { tempError.scopes = t("errorEmpty"); }
    }

    if (Object.keys(tempError).length > 0) {
      setSave(false);
    } else if (
      currentType === "http" &&
      (isNew.loginRoute || isNew.usernameValue || isNew.passwordValue)
    ) {
      setSave(false);
    } else if (
      currentType === "oauth2.0" &&
      (isNew.tokenUrl || isNew.clientId || isNew.clientSecret || isNew.scopes)
    ) {
      setSave(false);
    } else {
      setSave(true);
    }
    setErrors(tempError);
  };

  const validateLoginAuth = () => {
    if (!isNew.loginRoute) {
      if (!edit?.payload.loginRoute || edit.payload.loginRoute === "") {
        return t("errorEmptyRoute");
      } else if (!validateUrlRoute(edit.payload.loginRoute)) {
        return t("errorRoute");
      } else if (
        arrayAuth?.some(
          (e) =>
            e.payload.loginRoute === edit?.payload?.loginRoute &&
            e._id !== edit._id,
        )
      ) {
        return t("errorRouteExists");
      }
    }
    return "";
  };

  const handleEditButton = () => {
    if (!editing) {
      const currentType = normalizeTypeValue(auth.type);
      let initialPayload;

      if (currentType === "http") {
        initialPayload = {
          username: {
            key: auth.payload.username?.key || "",
            value: "",
          },
          password: {
            key: auth.payload.password?.key || "",
            value: "",
          },
          loginRoute: auth.payload.loginRoute || "",
          tokenPrefix: auth.payload.tokenPrefix || "",
        };
      } else {
        initialPayload = {
          tokenUrl: auth.payload.tokenUrl || "",
          clientId: auth.payload.clientId || "",
          clientSecret: "",
          scopes: auth.payload.scopes || "",
          tokenPrefix: "Bearer",
        };
      }

      setEdit({
        _id: auth._id,
        type: auth.type,
        payload: initialPayload,
        tokenName:
          currentType === "http" ? auth.tokenName || "" : "Authorization",
        new: auth.new,
        editing: auth.editing,
      });
    }
    setEditing(true);
  };

  const handleEditInput = (value, fieldName) => {
    let editAuth = { ...edit };
    const tempIsNew = { ...isNew };
    const tempTouched = { ...touched };
    if (fieldName === "type") {
      editAuth.type = value;
      // Reiniciar el payload según el tipo seleccionado
      if (value === "http") {
        editAuth.payload = {
          username: { key: "", value: "" },
          password: { key: "", value: "" },
          loginRoute: "",
          tokenPrefix: "",
        };
        editAuth.tokenName = "";
      } else if (value === "oauth2.0") {
        editAuth.payload = {
          tokenUrl: "",
          clientId: "",
          clientSecret: "",
          scopes: "",
          tokenPrefix: "Bearer",
        };
        editAuth.tokenName = "Authorization";
      }
      setTouched({});
      setEdit(editAuth);
      return;
    } else if (fieldName === "loginRoute") {
      if (isNew.loginRoute) {
        tempIsNew.loginRoute = false;
      }
      editAuth.payload.loginRoute = value;
    } else if (fieldName === "tokenUrl") {
      if (isNew.tokenUrl) {
        tempIsNew.tokenUrl = false;
      }
      editAuth.payload.tokenUrl = value;
    } else if (fieldName === "clientId") {
      if (isNew.clientId) {
        tempIsNew.clientId = false;
      }
      editAuth.payload.clientId = value;
    } else if (fieldName === "clientSecret") {
      if (isNew.clientSecret) {
        tempIsNew.clientSecret = false;
      }
      editAuth.payload.clientSecret = value;
    } else if (fieldName === "scopes") {
      if (isNew.scopes) {
        tempIsNew.scopes = false;
      }
      editAuth.payload.scopes = value;
    } else if (fieldName.includes("username")) {
      if (fieldName.includes("key") || fieldName.includes("Key")) {
        editAuth.payload.username.key = value;
      } else if (fieldName.includes("value") || fieldName.includes("Value")) {
        setEditUsernameValue(true);
        if (isNew.usernameValue) {
          tempIsNew.usernameValue = false;
        }
        editAuth.payload.username.value = value;
      }
    } else if (fieldName.includes("password")) {
      if (fieldName.includes("key") || fieldName.includes("Key")) {
        editAuth.payload.password.key = value;
      } else if (fieldName.includes("value") || fieldName.includes("Value")) {
        setEditPasswordValue(true);
        if (isNew.passwordValue) {
          tempIsNew.passwordValue = false;
        }
        editAuth.payload.password.value = value;
      }
    }
    if (fieldName === "tokenPrefix") {
      if (isNew.tokenPrefix) {
        tempIsNew.tokenPrefix = false;
      }
      editAuth.payload.tokenPrefix = value;
    }
    if (fieldName === "tokenName") {
      if (isNew.tokenName) {
        tempIsNew.tokenName = false;
      }
      editAuth.tokenName = value;
    }
    tempTouched[fieldName] = true;
    setTouched(tempTouched);
    setIsNew(tempIsNew);
    setEdit(editAuth);
  };

  const handleSaveEdit = () => {
    const tempEdit = edit;
    const currentType = normalizeTypeValue(tempEdit.type);

    if (currentType === "http") {
      if (tempEdit.payload.username?.value === "**********") {
        delete tempEdit.payload.username.value;
      }
      if (tempEdit.payload.password?.value === "**********") {
        delete tempEdit.payload.password.value;
      }
    } else if (currentType === "oauth2.0") {
      if (tempEdit.payload.clientSecret === "**********") {
        delete tempEdit.payload.clientSecret;
      }
      // Establecer valores por defecto para OAUTH2.0
      tempEdit.payload.tokenPrefix = "Bearer";
      tempEdit.tokenName = "Authorization";
    }

    saveAuth(tempEdit);
    setEdit({});
    setEditing(false);
    setEditUsernameValue(false);
    setEditPasswordValue(false);
  };

  const handleCancelEdit = () => {
    const payload = auth.payload;
    setEditing(false);
    if (auth.new) {
      setEdit({});
      modalDeleteAuth(auth);
    } else {
      const currentType = normalizeTypeValue(auth.type);
      let originalPayload;

      if (currentType === "http") {
        originalPayload = {
          username: {
            key: payload.username?.key || "",
            value: payload.username?.value || "",
          },
          password: {
            key: payload.password?.key || "",
            value: payload.password?.value || "",
          },
          loginRoute: payload.loginRoute || "",
          tokenPrefix: payload.tokenPrefix || "",
        };
      } else {
        originalPayload = {
          tokenUrl: payload.tokenUrl || "",
          clientId: payload.clientId || "",
          clientSecret: payload.clientSecret || "",
          scopes: payload.scopes || "",
          tokenPrefix: "Bearer",
        };
      }

      const originalauth = {
        type: auth.type,
        payload: originalPayload,
        tokenName:
          currentType === "http" ? auth.tokenName || "" : "Authorization",
      };
      setEdit(originalauth);
    }
  };

  const renderHttpFields = (payload, tokenName) => (
    <>
      <TextField
        id="loginRoute"
        value={payload.loginRoute}
        onChange={(e) => handleEditInput(e.target.value, "loginRoute")}
        label={t("loginauth")}
        error={touched.loginRoute && Boolean(errors?.loginRoute)}
        helperText={touched.loginRoute && errors?.loginRoute}
        disabled={!editing}
        variant="standard"
        fullWidth
        sx={{ marginTop: "8px" }}
      />
      <TextField
        id="usernameKey"
        value={payload.username?.key}
        onChange={(e) => handleEditInput(e.target.value, "usernameKey")}
        label={t("usernameKey")}
        error={touched.usernameKey && Boolean(errors?.usernameKey)}
        helperText={touched.usernameKey && errors?.usernameKey}
        disabled={!editing}
        variant="standard"
        fullWidth
        sx={{ marginTop: "8px" }}
      />
      <TextField
        id="usernameValue"
        value={!editing ? "**********" : payload.username?.value}
        onChange={(e) => handleEditInput(e.target.value, "usernameValue")}
        label={t("usernameValue")}
        error={touched.usernameValue && Boolean(errors?.usernameValue)}
        helperText={touched.usernameValue && errors?.usernameValue}
        disabled={!editing}
        variant="standard"
        fullWidth
        sx={{ marginTop: "8px" }}
      />
      <TextField
        id="passwordKey"
        value={payload.password?.key}
        onChange={(e) => handleEditInput(e.target.value, "passwordKey")}
        label={t("passwordKey")}
        error={touched.passwordKey && Boolean(errors?.passwordKey)}
        helperText={touched.passwordKey && errors?.passwordKey}
        disabled={!editing}
        variant="standard"
        fullWidth
        sx={{ marginTop: "8px" }}
      />
      <TextField
        id="passwordValue"
        value={!editing ? "**********" : payload.password?.value}
        onChange={(e) => handleEditInput(e.target.value, "passwordValue")}
        label={t("passwordValue")}
        error={touched.passwordValue && Boolean(errors?.passwordValue)}
        helperText={touched.passwordValue && errors?.passwordValue}
        fullWidth
        disabled={!editing}
        variant="standard"
        sx={{ marginTop: "8px" }}
      />
      <TextField
        id="tokenPrefix"
        value={payload.tokenPrefix}
        onChange={(e) => handleEditInput(e.target.value, "tokenPrefix")}
        label={t("tokenPrefix")}
        error={touched.tokenPrefix && Boolean(errors?.tokenPrefix)}
        helperText={touched.tokenPrefix && errors?.tokenPrefix}
        disabled={!editing}
        variant="standard"
        fullWidth
        sx={{ marginTop: "8px" }}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        id="tokenName"
        value={tokenName}
        onChange={(e) => handleEditInput(e.target.value, "tokenName")}
        label={t("tokenName")}
        error={touched.tokenName && Boolean(errors?.tokenName)}
        helperText={touched.tokenName && errors?.tokenName}
        disabled={!editing}
        variant="standard"
        fullWidth
        sx={{ marginTop: "8px" }}
      />
    </>
  );

  const renderOAuthFields = (payload) => (
    <>
      <TextField
        id="tokenUrl"
        value={payload.tokenUrl}
        onChange={(e) => handleEditInput(e.target.value, "tokenUrl")}
        label="Token URL"
        error={touched.tokenUrl && Boolean(errors?.tokenUrl)}
        helperText={touched.tokenUrl && errors?.tokenUrl}
        disabled={!editing}
        variant="standard"
        fullWidth
        sx={{ marginTop: "8px" }}
      />
      <TextField
        id="clientId"
        value={payload.clientId}
        onChange={(e) => handleEditInput(e.target.value, "clientId")}
        label="Client ID"
        error={touched.clientId && Boolean(errors?.clientId)}
        helperText={touched.clientId && errors?.clientId}
        disabled={!editing}
        variant="standard"
        fullWidth
        sx={{ marginTop: "8px" }}
      />
      <TextField
        id="clientSecret"
        value={!editing ? "**********" : payload.clientSecret}
        onChange={(e) => handleEditInput(e.target.value, "clientSecret")}
        label="Client Secret"
        error={touched.clientSecret && Boolean(errors?.clientSecret)}
        helperText={touched.clientSecret && errors?.clientSecret}
        disabled={!editing}
        variant="standard"
        fullWidth
        sx={{ marginTop: "8px" }}
      />
      <TextField
        id="scopes"
        value={payload.scopes}
        onChange={(e) => handleEditInput(e.target.value, "scopes")}
        label="Scopes"
        error={touched.scopes && Boolean(errors?.scopes)}
        helperText={touched.scopes && errors?.scopes}
        disabled={!editing}
        variant="standard"
        fullWidth
        sx={{ marginTop: "8px" }}
      />
      <TextField
        id="tokenPrefix"
        value="Bearer"
        label="Token Prefix"
        disabled={true}
        variant="standard"
        fullWidth
        sx={{ marginTop: "px" }}
      />
      <TextField
        id="tokenName"
        value="Authorization"
        label="Token Name"
        disabled={true}
        variant="standard"
        fullWidth
        sx={{ marginTop: "8px", marginBottom: "8px" }}
        InputLabelProps={{ shrink: true }}
      />
    </>
  );

  const renderPayload = (auth) => {
    const payload = editing ? edit.payload : auth.payload;
    const tokenName = editing ? edit.tokenName : auth.tokenName;
    const currentType = normalizeTypeValue(editing ? edit?.type : auth.type);
    return (
      <Box>
        <FormControl fullWidth sx={{ marginTop: "8px" }}>
          <InputLabel id="type-label">{t("nameType")}</InputLabel>
          <Select
            labelId="type-label"
            id="type"
            value={currentType}
            label={t("nameType")}
            onChange={(e) => handleEditInput(e.target.value, "type")}
            disabled={!editing}
            variant="standard">
            <MenuItem value="http">HTTP</MenuItem>
            <MenuItem value="oauth2.0">OAUTH2.0</MenuItem>
          </Select>
        </FormControl>
        {currentType === "http"
          ? renderHttpFields(payload, tokenName)
          : renderOAuthFields(payload)}
      </Box>
    );
  };

  const renderButtonsEdit = (index) => {
    return (
      <Box
        sx={{
          marginTop: "5px",
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-end",
          justifyContent: "flex-end",
        }}>
        <Button
          id={`saveEditButton_${index}`}
          variant="contained"
          disabled={!save}
          onClick={() => handleSaveEdit(index)}
          sx={{ margin: "8px" }}>
          {t("saveEditButon")}
        </Button>
        <Button
          id={`cancelEditButton_${index}`}
          variant="contained"
          onClick={() => handleCancelEdit(index)}
          sx={{ margin: "8px" }}>
          {t("cancelEditButon")}
        </Button>
      </Box>
    );
  };

  return (
    <Grid
      container
      columns={7}
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
      }}>
      <Grid item md={5}>
        {renderPayload(auth)}
      </Grid>
      <Grid
        item
        xs={1}
        rows={12}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}>
        <Grid
          item
          xs={2}
          columns={24}
          sx={{ display: "flex", flexDirection: "row" }}>
          <Grid
            item
            xs={8}
            sx={{ display: "flex", flexDirection: "row", marginRight: "20%" }}>
            <Tooltip
              title={
                userPermissions ? t("editTooltip") : t("noPermissionMessage")
              }
              sx={{
                display: "flex",
                "&:hover": { color: "#4e8cff" },
              }}>
              <span>
                <IconButton
                  id="iconEdit"
                  disabled={!userPermissions}
                  onClick={() => handleEditButton()}
                  sx={{ height: "50%" }}>
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
          <Grid
            item
            xs={8}
            sx={{ display: "flex", flexDirection: "row", marginLeft: "20%" }}>
            <Tooltip
              title={
                userPermissions ? t("deleteTooltip") : t("noPermissionMessage")
              }
              sx={{
                display: "flex",
                "&:hover": { color: "#4e8cff" },
              }}>
              <span>
                <IconButton
                  id="iconDelete"
                  disabled={!userPermissions}
                  onClick={() => modalDeleteAuth(auth)}
                  sx={{ height: "50%" }}>
                  <Delete
                    sx={{
                      color: userPermissions ? "deleteIcon.color" : "grey",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
      {editing ? renderButtonsEdit(_id) : null}
    </Grid>
  );
};

AuthItem.propTypes = {
  auth: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    payload: PropTypes.shape({
      username: PropTypes.shape({
        key: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      password: PropTypes.shape({
        key: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      loginRoute: PropTypes.string.isRequired,
      tokenPrefix: PropTypes.string.isRequired,
    }).isRequired,
    tokenName: PropTypes.string.isRequired,
    new: PropTypes.bool.isRequired,
    editing: PropTypes.bool.isRequired,
    add: PropTypes.bool,
  }).isRequired,
  _id: PropTypes.string.isRequired,
  arrayAuth: PropTypes.array.isRequired,
  modalDeleteAuth: PropTypes.func.isRequired,
  saveAuth: PropTypes.func.isRequired,
  userPermissions: PropTypes.object.isRequired,
};

export default AuthItem;
